import type { FC } from 'react'

import type { AssetType } from '@skytech/types'
import { AssetFallbackIcon, StyledText, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useQuery } from '@tanstack/react-query'
import CartCheckIcon from 'mdi-react/CartCheckIcon'
import MinusIcon from 'mdi-react/MinusIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { twJoin } from 'tailwind-merge'

import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'

import './section-products.scss'

const Product: FC<{
  id: number
  name: string
  alias: string | null
  price: number
  quantity: number
  currency: string | null
  imgUrl: string | null
  isParentProduct: boolean
  isTransportationProduct: boolean
  isMandatoryProduct: boolean
  quantityLimit: number
  assetType: AssetType | null
  onActiveStepSet?: (newActiveStep: number) => void
}> = ({
  name,
  alias,
  price,
  id,
  imgUrl,
  currency,
  quantityLimit,
  isParentProduct,
  isTransportationProduct,
  isMandatoryProduct,
  assetType,
  onActiveStepSet,
}) => {
  const translate = useTranslation()
  const shopCtx = useShopNewOrderContext()
  const setSearchFilter = shopCtx.setSearchFilter
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById
  const [selectedProductIds, setSelectedProductIds] = shopCtx.selectedProductIds

  const policyListQuery = useQuery(commonQueries.policyList(), commonEcomPolicyResources.getList)
  const isRelationsIgnored = Boolean(
    policyListQuery.data?.find(({ id }) => id === selectedPolicyId)?.isRelationsIgnored
  )

  // 2 is Products step and 3 is Accessories step
  const newActiveStep = isParentProduct || isRelationsIgnored ? 2 : 3
  const quantity = productQuantityById[id]

  const handleClick = () => {
    setSearchFilter(name)

    if (onActiveStepSet) {
      onActiveStepSet(newActiveStep)
    }
  }

  const isThreeStepIT = useIsThreeStepIT()
  const isAdmin = useIsAdmin()
  const isQuantitySelectionAllowed = isAdmin || quantityLimit > 1
  const isQuantitySelectionAvailable =
    !isTransportationProduct && !isMandatoryProduct && isQuantitySelectionAllowed
  const isQuantityLimitReached = quantity + 1 > quantityLimit

  return (
    <button className="step-confirm__product" onClick={handleClick}>
      <div className="relative py-1">
        <div className="step-confirm__product__img">
          <ImageWithIconFallback
            src={imgUrl ?? ''}
            alt="product"
            fallbackIcon={<AssetFallbackIcon assetType={assetType ?? 'OTHER'} />}
          />
        </div>

        {isMandatoryProduct && (
          <div
            id={`is-mandatory-product-${id}`}
            className={twJoin(
              'absolute -right-1 -top-2 rounded bg-white',
              isThreeStepIT ? 'text-threeStepIT-failure' : 'fill-red-500'
            )}
          >
            <Tooltip
              anchorId={`is-mandatory-product-${id}`}
              content={translate('Mandatory product')}
            />
            <CartCheckIcon
              className={twJoin(isThreeStepIT ? 'text-threeStepIT-failure' : 'fill-red-500')}
            />
          </div>
        )}
      </div>

      <div className="step-confirm__product__info">
        <div className="step-confirm__product__name">{alias || name}</div>
        <div className="step-confirm__product__cost">
          <StyledText size="xl" marginRight={8}>
            {quantity}x
          </StyledText>
          <FormattedNumberWithCurrency amount={price} currency={currency} />
        </div>
      </div>

      {isQuantitySelectionAvailable && Boolean(quantityLimit > 1) && (
        <div className="absolute right-2 top-16 flex items-center justify-center gap-2 text-base">
          <Button
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            size="extraSmall"
            variant="tertiary"
            Icon={<MinusIcon className="m-0" />}
            onClick={e => {
              e.stopPropagation()
              if (quantity === 1) {
                const filteredSelectedProductIds = selectedProductIds?.filter(
                  selectedProductIdItem => selectedProductIdItem !== id
                )
                setSelectedProductIds(filteredSelectedProductIds || null)
                return
              }
              setProductQuantityById({ ...productQuantityById, [id]: quantity - 1 })
            }}
          />
          <div>{quantity}</div>
          <Button
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            variant="tertiary"
            size="extraSmall"
            disabled={isQuantityLimitReached}
            Icon={<PlusIcon className="m-0" />}
            onClick={e => {
              e.stopPropagation()
              setProductQuantityById({ ...productQuantityById, [id]: quantity + 1 })
            }}
          />
        </div>
      )}
    </button>
  )
}

export default Product
